import DialogData from './DialogData.js';
import DialogBox from './DialogBox.js';
import ChatBox from '../chat/ChatBox.js';
import ChatService from '../chat/ChatService.js';
import ChatPrompts from '../chat/ChatPrompts.js';
import ApiKeyPrompt from '../chat/ApiKeyPrompt.js';
import Debug from '../utils/Debug.js';

/**
 * DialogManager - Orchestrates NPC dialog interactions.
 *
 * Checks proximity each frame, shows interaction prompt,
 * manages dialog state traversal, and pauses/resumes gameplay.
 * Routes AI-chat NPCs to ChatBox + ChatService instead of static DialogBox.
 */
export default class DialogManager {
  static INTERACTION_DISTANCE = 2.5;

  /**
   * @param {Object} options
   * @param {NPCManager} options.npcManager
   * @param {InputManager} options.inputManager
   */
  constructor(options = {}) {
    this.npcManager = options.npcManager;
    this.inputManager = options.inputManager;
    this.player = null;

    // Dialog state
    this.dialogBox = new DialogBox();
    this.dialogBox._onOutsideClick = () => this.endDialog();
    this.chatBox = new ChatBox();
    this.chatService = new ChatService();
    this.isActive = false;
    this._isChatMode = false;
    this.currentNPC = null;
    this.currentTree = null;
    this.currentNodeId = null;

    // Proximity state
    this._nearestNPC = null;
    this._interactHeld = false;

    // Touch device detection
    this._isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    // Network callback: broadcast NPC dialog events to authority (set by main.js)
    this.onNPCAction = null;

    // Shop callback: open shop UI (set by main.js)
    this.onShopOpen = null;
    this._isShopMode = false;

    // Key trade callback: trade dungeon keys for mystery reward (set by main.js)
    this.onTradeKeys = null;

    // Interaction prompt
    this.interactPromptEl = null;
    this._createInteractPrompt();

    // Keyboard listener for dialog navigation
    this._onKeyDown = this._handleKeyDown.bind(this);
    window.addEventListener('keydown', this._onKeyDown);
  }

  setPlayer(player) {
    this.player = player;
  }

  _createInteractPrompt() {
    this.interactPromptEl = document.createElement('div');
    this.interactPromptEl.className = 'dialog-interact-prompt';
    this.interactPromptEl.textContent = this._isTouchDevice ? 'X' : 'E';
    this.interactPromptEl.style.display = 'none';
    // Not appended here — reparented to nearest NPC's sprite element in update()
    this._promptParentSprite = null;
  }

  /**
   * Called by mobile X button press.
   * Starts dialog if near NPC, or advances/confirms if dialog is active.
   */
  onMobileInteract() {
    if (this.isActive) {
      if (this._isChatMode) return; // ChatBox has its own input
      this.dialogBox._handleConfirm();
    } else if (this._nearestNPC) {
      this.startDialog(this._nearestNPC);
    }
  }

  /**
   * Called each frame from the game loop.
   * @param {number} deltaTime
   * @param {Object} camera
   */
  update(deltaTime, camera) {
    if (this.isActive) {
      // Auto-close dialog if player drifts too far from NPC (e.g. falling after jump)
      if (this.currentNPC && this.player) {
        const dx = this.player.x - this.currentNPC.x;
        const dz = this.player.z - this.currentNPC.z;
        const dist = Math.sqrt(dx * dx + dz * dz);
        if (dist > DialogManager.INTERACTION_DISTANCE * 1.5) {
          this.endDialog();
        }
      }
      return;
    }
    if (!this.player) return;

    // Find nearest interactable NPC
    let nearestNPC = null;
    let nearestDist = DialogManager.INTERACTION_DISTANCE;

    for (const [id, { character }] of this.npcManager.npcs) {
      // Skip hostile/dead NPCs — they can't be talked to
      if (character.combatStats && (character.combatStats.isHostile || character.combatStats.isDead)) continue;

      const dx = character.x - this.player.x;
      const dy = character.y - this.player.y;
      const dz = character.z - this.player.z;
      const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);

      if (dist < nearestDist) {
        nearestDist = dist;
        nearestNPC = character;
      }
    }

    this._nearestNPC = nearestNPC;

    // Update interact prompt — reparent to nearest NPC's sprite for depth sorting
    if (nearestNPC && this.interactPromptEl) {
      const spriteEl = nearestNPC.sprite?.element;
      if (spriteEl && this._promptParentSprite !== spriteEl) {
        spriteEl.appendChild(this.interactPromptEl);
        this._promptParentSprite = spriteEl;
      }
      this.interactPromptEl.style.display = 'block';
    } else if (this.interactPromptEl) {
      this.interactPromptEl.style.display = 'none';
      this._promptParentSprite = null;
    }

    // Check E key (edge-triggered)
    const eDown = this.inputManager.keys.e || this.inputManager.keys['E'];
    if (eDown && !this._interactHeld && nearestNPC) {
      this.startDialog(nearestNPC);
    }
    this._interactHeld = !!eDown;
  }

  /**
   * Start a dialog with an NPC.
   * All NPCs go through the static dialog tree first.
   * Trees can transition to AI chat via the special '_chat' node.
   * @param {Character} npcCharacter
   */
  startDialog(npcCharacter) {
    if (this.isActive) return;

    const characterName = npcCharacter.characterName;
    const tree = DialogData.getTree(characterName);
    if (!tree || !tree.start) {
      Debug.warn('Dialog', `No dialog tree for ${characterName}`);
      return;
    }

    this.isActive = true;
    this.currentNPC = npcCharacter;
    this.currentTree = tree;
    window.game?.audioSystem?.audio?.playUiOpen();

    // Hide toolbar during dialog
    document.body.classList.add('dialog-active');

    // Pause NPC
    this._pauseNPC(npcCharacter);

    // Pause player
    this._pausePlayer();

    // Both characters face each other
    this._faceNPCTowardPlayer(npcCharacter);
    this._facePlayerTowardNPC(npcCharacter);

    // Broadcast to authority so NPC freezes for all peers
    this.onNPCAction?.({
      type: 'npc-dialog-start',
      npcId: npcCharacter.id,
      px: this.player?.x,
      pz: this.player?.z
    });

    // Hide interact prompt
    if (this.interactPromptEl) {
      this.interactPromptEl.style.display = 'none';
    }

    Debug.log('Dialog', `Started dialog with ${characterName}`);
    this._showNode('start');
  }

  /**
   * Open the AI chat as a sub-state within an active dialog.
   * When chat closes, returns to the dialog tree's 'menu' node.
   */
  async _openChat() {
    const characterName = this.currentNPC.characterName;

    // Check for server proxy or API key; prompt if neither available
    await this.chatService._isProxyAvailable();
    if (!this.chatService.hasApiKey()) {
      const key = await ApiKeyPrompt.show();
      if (!key) {
        // User cancelled — return to dialog menu
        this._showNode('menu');
        return;
      }
      this.chatService.setApiKey(key);
    }

    // Hide the dialog box and switch to chat mode
    this.dialogBox.hide();
    this._isChatMode = true;

    const systemPrompt = ChatPrompts.getPrompt(characterName);
    const greeting = ChatPrompts.getGreeting(characterName);
    const history = this.chatService.getHistory(characterName, systemPrompt);
    const displayHistory = history.filter(m => m.role !== 'system');
    const showGreeting = displayHistory.length === 0 ? greeting : null;

    const displayName = this.currentNPC.displayName || characterName;
    this.chatBox.show(
      displayName,
      displayHistory,
      showGreeting,
      // onSend callback
      async (messageText) => {
        this.chatBox.setLoading(true);
        try {
          const reply = await this.chatService.sendMessage(
            characterName, messageText, systemPrompt
          );
          this.chatBox.addMessage('assistant', reply);
        } catch (err) {
          if (err.message === 'SESSION_LIMIT') {
            this.chatBox.addMessage('assistant',
              "Whoa dude, we've been chatting a ton! My brain's fried like a beach day without sunscreen. Catch me again next time you visit!");
          } else if (err.message === 'TOO_FAST') {
            this.chatBox.addMessage('assistant',
              "Easy there bro, let me catch my breath! Gimme a sec.");
          } else {
            this.chatBox.showError(err.message);
          }
        } finally {
          this.chatBox.setLoading(false);
        }
      },
      // onClose callback — return to dialog menu
      () => {
        this.chatBox.hide();
        this._isChatMode = false;
        this._showNode('menu');
      }
    );

    Debug.log('Dialog', `Opened AI chat with ${characterName}`);
  }

  _showNode(nodeId) {
    if (nodeId === '_end') {
      this.endDialog();
      return;
    }

    if (nodeId === '_chat') {
      this._openChat();
      return;
    }

    if (nodeId === '_shop') {
      this._openShop();
      return;
    }

    if (nodeId === '_trade_keys_bronze' || nodeId === '_trade_keys_silver' || nodeId === '_trade_keys_gold') {
      const tier = nodeId.replace('_trade_keys_', '');
      this._executeKeyTrade(tier);
      return;
    }

    const node = this.currentTree[nodeId];
    if (!node) {
      Debug.warn('Dialog', `Node "${nodeId}" not found`);
      this.endDialog();
      return;
    }

    this.currentNodeId = nodeId;
    const speaker = this.currentNPC.displayName || node.speaker || this.currentNPC.characterName;

    if (node.type === 'choice') {
      this.dialogBox.showChoice(
        speaker,
        node.text,
        node.choices,
        (choiceIndex) => {
          const choice = node.choices[choiceIndex];
          this._showNode(choice.next);
        }
      );
    } else {
      this.dialogBox.showText(
        speaker,
        node.text,
        () => this._showNode(node.next || '_end')
      );
    }
  }

  /**
   * Open the shop UI as a sub-state within an active dialog.
   * When shop closes, returns to the dialog tree's 'offer' node.
   */
  _openShop() {
    this.dialogBox.hide();
    this._isShopMode = true;
    if (this.onShopOpen) {
      this.onShopOpen(this.currentNPC, () => {
        // Return to dialog callback
        this._isShopMode = false;
        this._showNode('offer');
      }, () => {
        // Close entirely callback
        this._isShopMode = false;
        this.endDialog();
      });
    }
  }

  /**
   * Execute the dungeon key trade. Calls back to main.js which checks
   * inventory, removes keys, rolls reward, then provides result text.
   */
  _executeKeyTrade(tier) {
    if (this.onTradeKeys) {
      this.onTradeKeys(this.currentNPC, tier, (resultText) => {
        this.dialogBox.showText('Merchant', resultText, () => {
          this._showNode('offer');
        });
      });
    }
  }

  endDialog() {
    if (!this.isActive) return;
    window.game?.audioSystem?.audio?.playUiClose();

    if (this._isChatMode) {
      this.chatBox.hide();
    } else if (this._isShopMode) {
      // Shop overlay handles its own cleanup
    } else {
      this.dialogBox.hide();
    }

    // Broadcast to authority so NPC resumes for all peers
    if (this.currentNPC) {
      this.onNPCAction?.({
        type: 'npc-dialog-end',
        npcId: this.currentNPC.id
      });
    }

    this._resumeNPC(this.currentNPC);
    this._resumePlayer();

    this.isActive = false;
    this._isChatMode = false;
    this._isShopMode = false;
    this.currentNPC = null;
    this.currentTree = null;
    this.currentNodeId = null;

    // Show toolbar again
    document.body.classList.remove('dialog-active');

    // Prevent immediate re-trigger
    this._interactHeld = true;

    Debug.log('Dialog', 'Dialog ended');
  }

  // ====== Pause / Resume ======

  _pauseNPC(npcCharacter) {
    if (npcCharacter.pathFollower && npcCharacter.pathFollower.isFollowing) {
      npcCharacter.pathFollower.stop();
    }
    npcCharacter.velocity.set(0, 0, 0);
    npcCharacter.setMovementState(false, false, true);
    if (npcCharacter.behavior) {
      npcCharacter.behavior._dialogPaused = true;
    }
  }

  _resumeNPC(npcCharacter) {
    if (!npcCharacter) return;
    if (npcCharacter.behavior) {
      npcCharacter.behavior._dialogPaused = false;
      npcCharacter.behavior._enterIdle();
    }
  }

  _pausePlayer() {
    if (!this.player) return;
    if (this.player.controller) {
      // Use dialogPaused instead of disable() so gravity/collision still run
      // (lets the player finish jump arcs naturally during dialog)
      this.player.controller.dialogPaused = true;
    }
    if (this.player.pathFollower && this.player.pathFollower.isFollowing) {
      this.player.pathFollower.stop();
    }
    // Only force idle if already on the ground — airborne players will
    // land naturally via the controller's updateAnimationState()
    if (this.player.isGrounded) {
      this.player.setMovementState(false, false, true);
    }
  }

  _resumePlayer() {
    if (!this.player) return;
    if (this.player.controller) {
      this.player.controller.dialogPaused = false;
    }
  }

  _faceNPCTowardPlayer(npcCharacter) {
    if (!this.player) return;
    const dx = this.player.x - npcCharacter.x;
    const dz = this.player.z - npcCharacter.z;
    // Match DirectionResolver.angleFromMovement convention: atan2(dz, dx)
    const angle = Math.atan2(dz, dx) * (180 / Math.PI);
    npcCharacter.setFacing(((angle % 360) + 360) % 360);
  }

  _facePlayerTowardNPC(npcCharacter) {
    if (!this.player) return;
    const dx = npcCharacter.x - this.player.x;
    const dz = npcCharacter.z - this.player.z;
    const angle = Math.atan2(dz, dx) * (180 / Math.PI);
    this.player.setFacing(((angle % 360) + 360) % 360);
  }

  // ====== Input Handling ======

  _handleKeyDown(e) {
    if (!this.isActive) return;

    // In chat mode, only handle Escape — ChatBox owns all other input
    if (this._isChatMode) {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        this.chatBox.hide();
        this._isChatMode = false;
        this._showNode('menu');
      }
      return;
    }

    // In shop mode, only handle Escape — shop UI owns all other input
    if (this._isShopMode) {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        this._isShopMode = false;
        this.endDialog();
      }
      return;
    }

    const key = e.key;

    if (key === ' ' || key === 'Enter' || key === 'e' || key === 'E') {
      e.preventDefault();
      e.stopPropagation();
      this.dialogBox._handleConfirm();
    } else if (key === 'ArrowUp' || key === 'w' || key === 'W') {
      e.preventDefault();
      e.stopPropagation();
      this.dialogBox.navigateChoice('up');
    } else if (key === 'ArrowDown' || key === 's' || key === 'S') {
      e.preventDefault();
      e.stopPropagation();
      this.dialogBox.navigateChoice('down');
    } else if (key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      this.endDialog();
    }
  }

  /**
   * Try to interact with an NPC (called from sprite click).
   * @param {Character} npcCharacter
   */
  tryInteract(npcCharacter) {
    if (this.isActive) return;
    if (!this.player) return;

    const dx = npcCharacter.x - this.player.x;
    const dy = npcCharacter.y - this.player.y;
    const dz = npcCharacter.z - this.player.z;
    const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);

    if (dist <= DialogManager.INTERACTION_DISTANCE) {
      this.startDialog(npcCharacter);
    }
  }

  destroy() {
    window.removeEventListener('keydown', this._onKeyDown);
    this.dialogBox.destroy();
    this.chatBox.destroy();
    if (this.interactPromptEl && this.interactPromptEl.parentElement) {
      this.interactPromptEl.parentElement.removeChild(this.interactPromptEl);
    }
  }
}
